<script setup>
const props = defineProps({
  items: {
    type: Array,
    required: true,
    default() {
      return []
    },
  },
  title: {
    type: String,
    required: false,
    default: '',
  },
  theme: {
    type: String,
    required: false,
    default: 'primary',
  },
})

const selectedSlide = ref(0)
const carousel = ref(null)

const itemsWithPagination = computed(() => {
  return props.items?.map((item, index) => {
    return {
      ...item,
      pagination: {
        current: index + 1,
        total: props.items.length,
      },
    }
  })
})

const uniqueKeyFromItems = computed(() => {
  return props.items
    ?.map((item) => {
      return item.id
    })
    .join('-')
})

const itemsToShow = computed(() => (props.theme === 'minimal' ? 1 : 1.5))
</script>

<template>
  <div
    class="flex flex-col items-center gap-6"
    :class="{
      'rounded-md bg-gray-900 pb-14 pt-6 shadow': theme === 'primary',
      '': theme === 'minimal',
    }"
  >
    <div v-if="title" class="font-extrabold uppercase text-white">
      {{ title }}
    </div>
    <div class="relative w-full h-full grow">
      <Carousel
        :key="uniqueKeyFromItems"
        ref="carousel"
        v-model="selectedSlide"
        :items-to-show="itemsToShow"
        wrap-around
      >
        <Slide
          v-for="(item, index) in itemsWithPagination"
          :key="`slide-${index}`"
        >
          <div
            class="relative flex h-full items-center justify-center overflow-hidden rounded-xl"
            :class="[
              { 'slide-active': index === selectedSlide },
              { 'opacity-30': index !== selectedSlide && theme !== 'minimal' },
            ]"
          >
            <BaseVisual :file="item" :autoplay="false" class="z-0" />
            <div
              v-if="theme === 'primary'"
              class="pagination absolute right-5 top-5 rounded-2xl bg-gray-900 px-5 py-2 font-extrabold text-white opacity-0"
            >
              {{ item.pagination.current }} / {{ item.pagination.total }}
            </div>
          </div>
        </Slide>
      </Carousel>
      <div
        v-if="items.length > 1"
        class="arrow-left absolute left-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110"
        :class="{
          'bg-gray-600 text-gray-100': theme === 'primary',
          'bg-secondary text-white': theme === 'minimal',
        }"
        @click="carousel.prev()"
      >
        <IconArrow direction="left" class="h-[10px] w-[10px]" />
      </div>
      <div
        v-if="items.length > 1"
        class="arrow-right absolute right-4 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110"
        :class="{
          'bg-gray-600 text-gray-100': theme === 'primary',
          'bg-secondary text-white': theme === 'minimal',
        }"
        @click="carousel.next()"
      >
        <IconArrow class="h-[10px] w-[10px]" />
      </div>
    </div>
  </div>
</template>

<style>
.slide-active .arrow-left,
.slide-active .arrow-right {
  @apply opacity-100;
}

.slide-active .pagination {
  @apply opacity-70;
}
</style>

<style scoped>
.carousel {
  @apply h-full;
}

.carousel :deep(.carousel__viewport) {
  @apply h-full;
}

.carousel :deep(.carousel__track) {
  @apply h-full;
}
</style>
